export interface OABSubOrder {
  id: string;
  letter?: string;
  folio: string;
  proveedor: string;
  totalUSD: number;
  totalBs: number;
  estadoGeneral: string;
  numeroCotizacion?: string;
  fechaEstimadaEntrega?: string;
  comprobanteUrl?: string | null;
  lineCount?: number;
}

export interface OABHeader {
  id?: string;
  folio: string; // OAB-YYYYMMDD-##
  fechaEmision: string;
  totalUSD: number;
  totalBs: number;
  tasaBCV: number;
  estadoGeneral: string;
  notas?: string;
  notasCompras?: string;
  proveedor?: string;
  cotizacion?: string;
  fechaEstimadaEntrega?: string;
  comprobanteUrl?: string;
  oabPadreId?: string | null;
  isMotherOAB?: boolean;
  isSubOrden?: boolean;
  subOrdenes?: OABSubOrder[];
  nivelUrgenciaGlobal?: string;
  resumenTiendasClientes?: string;
}

export interface OABLineItem {
  id?: string;
  nombre: string;
  codigo?: string;
  categoriaMaterial?: string;
  insumoId?: string;
  dashboardId?: string;
  proveedor?: string;
  proveedorNombre?: string;
  proveedorAdjudicado?: string;
  subOrdenFolio?: string | null;
  subOrdenId?: string | null;
  cantidadStock: number;
  stockMinimo: number;
  deficit: number;
  cantidadSugerida: number;
  cantidadSolicitada: number;
  cantidadAprobada?: number;
  cantidadRecibida?: number;
  cantidadRecibidaHoy?: number;
  cantidadRecibidaPrevia?: number;
  cantidadRechazada?: number;
  backorderPendiente?: number;
  costoUnitarioUSD: number;
  costoAprobadoUSD?: number;
  subtotalUSD: number;
  prioridad: string;
  pedidoId?: string;
  proyectoId?: string;
  proyectoNombre?: string;
  tiendaNombre?: string;
  clienteNombre?: string;
  originalPedidoId?: string;
  originalProyectoNombre?: string;
  parentSolicitudId?: string;
  originalCantidadAprobada?: number;
  isSplitChild?: boolean;
  notasDiscrepancia?: string;
  toleranciaExcedente?: boolean;
  empaqueComercial?: string;
  factorEmpaque?: number;
  paquetesSugeridos?: number;
  cantidadComercialSugerida?: number;
  rotularEtiqueta?: boolean;
  bultos?: number;
  cantEnBulto?: number;
  bultoPico?: number;
  desgloseBultos?: number[];
  unidadMedida?: string;
  uomCompra?: string;
  factorUoM?: number;
  cantidadCompra?: number;
  costoCompraUSD?: number;
  formulaUoM?: string;
  actionOnRemaining?: 'BACKORDER' | 'DISCARD' | 'REQUOTE_ANOTHER_VENDOR';
  motivoSaldo?: string;
  rutaLogistica?: 'VIA_GALPON' | 'DIRECTO_OBRA' | 'SERVICIO_SITIO' | 'NO_APLICA';
  orderLineId?: string | null;
  isDirectOrService?: boolean;
}

export interface PackagingSuggestion {
  empaqueComercial: string;
  factorEmpaque: number;
  paquetesSugeridos: number;
  cantidadComercialSugerida: number;
}

export function computePackagingSuggestion(nombre: string, categoria?: string, deficit: number = 0): PackagingSuggestion {
  const lowerName = (nombre || '').toLowerCase();
  const lowerCat = (categoria || '').toLowerCase();
  const def = Math.max(1, Math.round(deficit));

  // 1. Tornillería / Remaches / Tuercas / Anclajes / Arandelas / Ramplús
  if (
    lowerName.includes('tornill') ||
    lowerName.includes('tuerca') ||
    lowerName.includes('rampl') ||
    lowerName.includes('remache') ||
    lowerName.includes('arandela') ||
    lowerCat.includes('tornill') ||
    lowerCat.includes('fijaci')
  ) {
    const factor = def > 100 ? 500 : 100;
    const packs = Math.ceil(def / factor);
    return {
      empaqueComercial: `Caja x ${factor} und`,
      factorEmpaque: factor,
      paquetesSugeridos: packs,
      cantidadComercialSugerida: packs * factor
    };
  }

  // 2. Perfilería Metálica / Tubos / Barras / Ángulos / Pletinas (Estándar 6.00 m)
  if (
    lowerName.includes('tubo') ||
    lowerName.includes('perfil') ||
    lowerName.includes('angulo') ||
    lowerName.includes('ángulo') ||
    lowerName.includes('pletina') ||
    lowerName.includes('barra') ||
    lowerName.includes('viga') ||
    lowerCat.includes('metal') ||
    lowerCat.includes('perfil')
  ) {
    const factor = 6; // 6 metros por barra comercial
    const packs = Math.ceil(def / factor);
    return {
      empaqueComercial: `Barra 6.0m`,
      factorEmpaque: factor,
      paquetesSugeridos: packs,
      cantidadComercialSugerida: packs * factor
    };
  }

  // 3. Pinturas / Solventes / Thinner / Pegamentos / Químicos
  if (
    lowerName.includes('pintura') ||
    lowerName.includes('thinner') ||
    lowerName.includes('solvente') ||
    lowerName.includes('pega') ||
    lowerName.includes('fondo') ||
    lowerName.includes('esmalte') ||
    lowerCat.includes('quimic') ||
    lowerCat.includes('químic') ||
    lowerCat.includes('acabado')
  ) {
    if (def >= 15) {
      const factor = 19; // Cuñete ~5 galones (~19 Litros)
      const packs = Math.ceil(def / factor);
      return {
        empaqueComercial: `Cuñete 5 Gal (19L)`,
        factorEmpaque: factor,
        paquetesSugeridos: packs,
        cantidadComercialSugerida: packs * factor
      };
    } else if (def >= 4) {
      const factor = 4; // Galón ~3.785 - 4L
      const packs = Math.ceil(def / factor);
      return {
        empaqueComercial: `Galón (4L)`,
        factorEmpaque: factor,
        paquetesSugeridos: packs,
        cantidadComercialSugerida: packs * factor
      };
    }
  }

  // 4. Tableros / MDF / Melaminas / Vidrios / Acrílicos
  if (
    lowerName.includes('mdf') ||
    lowerName.includes('melamina') ||
    lowerName.includes('lamina') ||
    lowerName.includes('lámina') ||
    lowerName.includes('vidrio') ||
    lowerName.includes('acrilic') ||
    lowerName.includes('acrílic') ||
    lowerCat.includes('madera') ||
    lowerCat.includes('tablero')
  ) {
    const factor = 1;
    const packs = Math.ceil(def);
    return {
      empaqueComercial: `Lámina estándar (1.22x2.44m)`,
      factorEmpaque: factor,
      paquetesSugeridos: packs,
      cantidadComercialSugerida: packs
    };
  }

  // 5. Electrodos / Alambre soldadura
  if (lowerName.includes('alambre') || lowerName.includes('soldad') || lowerName.includes('electrodo')) {
    const factor = 15; // Rollo soldadura 15kg o caja electrodos 5kg
    const packs = Math.ceil(def / factor);
    return {
      empaqueComercial: `Rollo/Caja x 15 kg`,
      factorEmpaque: factor,
      paquetesSugeridos: packs,
      cantidadComercialSugerida: packs * factor
    };
  }

  // Fallback: Pieza / Unidad
  return {
    empaqueComercial: `Pieza suelta`,
    factorEmpaque: 1,
    paquetesSugeridos: def,
    cantidadComercialSugerida: def
  };
}

export interface OrderReference {
  id: string;
  codigo: string;
  cliente: string;
  proyecto: string;
  proyectoId?: string;
  tipo: 'PED' | 'PRS' | 'FAC' | 'OBRA' | 'OE' | string;
  fecha?: string;
  estado?: string;
  estadoAuditoriaBOM?: string;
  toleranciaObraPct?: number;
}

export function computeOABUrgencySummary(lines: Array<{ prioridad?: string }>): string {
  if (!lines || lines.length === 0) return '🔵 Media / Normal';
  let urgentCount = 0;
  let highCount = 0;
  let orderCount = 0;

  for (const l of lines) {
    const p = (l.prioridad || '').toLowerCase().trim();
    if (p.includes('urgent') || p.includes('crític') || p.includes('critic')) {
      urgentCount++;
    } else if (p.includes('alta')) {
      highCount++;
    } else if (p.includes('pedido')) {
      orderCount++;
    }
  }

  if (urgentCount > 0) {
    return urgentCount === lines.length
      ? `🚨 Crítica / Urgente (${urgentCount} renglones)`
      : `🚨 Crítica / Urgente (${urgentCount} de ${lines.length} renglones)`;
  }
  if (highCount > 0) {
    return highCount === lines.length
      ? `🟠 Alta (${highCount} renglones)`
      : `🟠 Alta (${highCount} de ${lines.length} renglones)`;
  }
  if (orderCount > 0) {
    return `🏪 Por Pedido de Tienda (${orderCount} renglones)`;
  }
  return '🔵 Media / Normal';
}

export function computeOABStoresSummary(
  lines: Array<{ proyectoNombre?: string; tiendaNombre?: string; clienteNombre?: string }>
): string {
  if (!lines || lines.length === 0) return '📦 Stock Común de Almacén (Reposición General)';
  const uniqueDestinations = new Set<string>();

  for (const l of lines) {
    const rawProj = (l.proyectoNombre || '').trim();
    const isGeneral =
      !rawProj ||
      rawProj === 'STK' ||
      rawProj.toLowerCase().includes('stock general') ||
      rawProj.toLowerCase().includes('general / stock') ||
      rawProj.toLowerCase().includes('almacén general');

    if (l.tiendaNombre && l.clienteNombre && !l.tiendaNombre.toLowerCase().includes('stock general')) {
      uniqueDestinations.add(`Tienda: ${l.tiendaNombre} • Cliente: ${l.clienteNombre}`);
    } else if (!isGeneral) {
      uniqueDestinations.add(rawProj);
    }
  }

  if (uniqueDestinations.size === 0) {
    return '📦 Stock Común de Almacén (Reposición General)';
  }
  return Array.from(uniqueDestinations).join(' | ');
}

// --- Tipos y Normalización Canónica de Prioridades (PLAN-049) ---
export type PriorityType = 'Urgente' | 'Alta' | 'Media' | 'Baja' | 'Por Pedido';

export function normalizePriority(p?: string): PriorityType {
  if (!p) return 'Media';
  const lower = p.toLowerCase().trim();
  if (lower.includes('urgente') || lower.includes('critico')) return 'Urgente';
  if (lower.includes('alta') || lower === 'nivel 1') return 'Alta';
  if (lower.includes('baja')) return 'Baja';
  if (lower.includes('pedido') || lower === 'mto') return 'Por Pedido';
  if (lower.includes('media') || lower === 'nivel 2') return 'Media';
  return 'Media';
}
